// Which sections make the meeting.
//
// The report is everything the assessment can say. The meeting is the part an
// advisor walks a household through, and it used to be the same list: present
// mode took every top-level section, fourteen slides in the sample, and the
// advisor clicked past holdings tables, extraction exceptions and a Roth
// illustration to reach the whiteboard. The advisor lead's own second meeting
// is shorter than that, and it is drawn more than it is read.
//
// So the meeting is an explicit list, in the order the September 3 session set
// out: confirm what the household said they wanted and the facts that matter,
// the short goal sheet for the chosen period, the cash-flow board, the tax
// pools and their board, the protection questions, and the open decisions.
// Everything else stays in the report, one checkbox away, and nothing is a
// mandatory slide just because the calculation exists.
//
// The list is a default, not a rule: the advisor's choices for a household
// override it, section by section, and are kept with the assessment on the
// device. A section that does not render for a household (no statements, so
// no holdings) is simply not there to include.

export const SECTIONS = Object.freeze([
  { id: 'opening', label: 'Where you stand, and what you told us', meet: true },
  { id: 'findings', label: 'Strengths, opportunities, weaknesses, threats', meet: false },
  { id: 'goal-sheet', label: 'Goals', meet: true },
  { id: 'tax-pools', label: 'Where your money sits today', meet: true },
  { id: 'income', label: 'What comes in, against what you spend', meet: false },
  { id: 'board-cashflow', label: 'The cash flow, drawn', meet: true },
  { id: 'tax-horizon', label: 'The part of the tax bill nobody has shown you', meet: false },
  { id: 'board-tax', label: 'The tax picture, drawn', meet: true },
  { id: 'holdings', label: 'What you actually own', meet: false },
  { id: 'style-box', label: 'Where your investments sit', meet: false },
  { id: 'behaviour', label: 'How your investments have behaved', meet: false },
  { id: 'fees', label: 'What your investments cost you', meet: false },
  { id: 'protection', label: 'Protection and estate questions', meet: true },
  { id: 'next-steps', label: 'Open decisions and the next step', meet: true },
]);

// Section ids carry hyphens; store keys do not, so a stored choice never
// depends on how a path helper splits a dotted key.
export const storeKeyFor = id => id.replace(/-/g, '_');

/** The set of section ids in the meeting, given the advisor's overrides. */
export function meetingIncludes(overrides) {
  const chosen = overrides && typeof overrides === 'object' ? overrides : {};
  const out = new Set();
  for (const s of SECTIONS) {
    const choice = chosen[storeKeyFor(s.id)];
    if (choice === true || (choice !== false && s.meet)) out.add(s.id);
  }
  return out;
}

/**
 * Whether a keystroke belongs to something the person is typing into. The
 * meeting's keyboard navigation (arrows, space, page keys) must never take a
 * keystroke away from an editor, or a space typed into a note turns the page
 * on a screen the client is looking at.
 */
export function isEditableTarget(target) {
  if (!target || typeof target !== 'object') return false;
  const tag = String(target.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  const role = typeof target.getAttribute === 'function' ? target.getAttribute('role') : null;
  return role === 'textbox' || role === 'combobox' || role === 'searchbox';
}
